import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CreateMeeting, MeetingKind, SeriesEvery, seriesDates, type CreateSeries, type Meeting, type MeetingSummary, type MemberAssembly } from '@syndiqia/governance/contracts';
import { defaultTitle } from '@syndiqia/governance/templates';
import { errorMessage, useApi } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../../components/ui';
import { meetingWhen, useGovernanceMutation, useMeetings, useOpenActions } from '../../governance-queries';
import { useI18n } from '../../i18n';
import { BoardComposition } from './Board';
import { GovernanceNav } from './GovernanceNav';
import { useSyndicate } from '../../session';

export function MeetingsPage() {
  const { t, locale, date } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const meetings = useMeetings();
  const actions = useOpenActions();
  if (!s.can('governance.read')) return s.can('governance.view') ? <MemberAssemblies /> : <Alert tone="info">{g.boardOnly}</Alert>;
  const now = Date.now();
  const upcoming = (meetings.data ?? []).filter((m) => Date.parse(m.scheduledAt) >= now - 86_400_000 && m.status !== 'closed' && m.status !== 'cancelled').reverse();
  const past = (meetings.data ?? []).filter((m) => !upcoming.includes(m));

  const list = (items: typeof upcoming) => (
    <ul className="meeting-list">
      {items.map((m) => (
        <li key={m.id}>
          <Link to={`${s.base}/governance/${m.id}`} className="meeting-card">
            <span className={`badge badge-meeting-${m.kind}`}>{g.kinds[m.kind]}</span>
            <strong>{m.title}</strong>
            <span className="muted small">{meetingWhen(m.scheduledAt, locale)}</span>
            <span className={`badge badge-mstatus-${m.status}`}>{g.statuses[m.status]}</span>
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <PageHeader
        title={g.title}
        intro={s.legalStructure === 'divided_coownership' ? g.intro : g.introNpo}
        actions={s.can('governance.manage') && <Disclosure label={g.newMeeting}>{(close) => <MeetingForm onDone={close} />}</Disclosure>}
      />
      {meetings.isPending && <Loading label={t.loading} />}
      {meetings.isError && <Alert>{errorMessage(meetings.error, t)}</Alert>}
      {meetings.data?.length === 0 && <Empty>{g.none}</Empty>}
      <GovernanceNav />
      <BoardComposition />
      {upcoming.length > 0 && (
        <section>
          <h2>{g.upcoming}</h2>
          {list(upcoming)}
        </section>
      )}
      {!!actions.data?.length && (
        <section className="panel">
          <h2>{g.openActions}</h2>
          <ul className="action-list">
            {actions.data.map((a) => (
              <li key={a.id}>
                <span>
                  {a.description}{' '}
                  <Link to={`${s.base}/governance/${a.meetingId}`} className="small muted">
                    ({a.meetingTitle})
                  </Link>
                </span>
                <span className="muted small">{[a.responsible, a.dueOn && date(a.dueOn)].filter(Boolean).join(' · ')}</span>
                <span />
              </li>
            ))}
          </ul>
        </section>
      )}
      {past.length > 0 && (
        <section>
          <h2>{g.past}</h2>
          {list(past)}
        </section>
      )}
    </>
  );
}

function MeetingForm({ onDone }: { onDone: () => void }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const g = t.governance;
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [values, setValues] = useState({ kind: 'board' as Meeting['kind'], title: defaultTitle('board', s.legalStructure, year), date: '', time: '19:00', location: '', repeat: 'none' as 'none' | SeriesEvery, count: '6' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useGovernanceMutation<CreateMeeting, Meeting>((api, base, input) => api(`${base}/governance/meetings`, { method: 'POST', body: input }));
  const series = useGovernanceMutation<CreateSeries, MeetingSummary[]>((api, base, input) => api(`${base}/governance/meetings/series`, { method: 'POST', body: input }));
  const count = Math.min(24, Math.max(2, Number(values.count) || 2));
  const dates = values.repeat !== 'none' && values.date ? seriesDates(values.date, values.repeat, count) : [];
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const setKind = (kind: Meeting['kind']) => setValues((v) => ({ ...v, kind, title: defaultTitle(kind, s.legalStructure, year) }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const scheduledAt = values.date ? new Date(`${values.date}T${values.time || '19:00'}`).toISOString() : '';
    const result = validate(CreateMeeting, { kind: values.kind, title: values.title, scheduledAt, location: values.location });
    setErrors(result.errors ? { ...result.errors, date: result.errors.scheduledAt ?? '' } : {});
    if (!result.data) return;
    if (dates.length) {
      const scheduledAts = dates.map((d) => new Date(`${d}T${values.time || '19:00'}`).toISOString());
      series.mutate({ kind: values.kind, title: values.title, location: values.location, scheduledAts }, { onSuccess: onDone });
    } else create.mutate(result.data, { onSuccess: (m) => (onDone(), navigate(`${s.base}/governance/${m.id}`)) });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={g.kind}>
          <select value={values.kind} onChange={(e) => setKind(e.target.value as Meeting['kind'])}>
            {MeetingKind.options.map((k) => (
              <option key={k} value={k}>
                {g.kinds[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={g.meetingTitle} error={errors.title}>
          <input value={values.title} onChange={set('title')} />
        </Field>
      </div>
      <div className="row">
        <Field label={g.date} error={errors.date || undefined}>
          <input type="date" value={values.date} onChange={set('date')} autoFocus />
        </Field>
        <Field label={g.time}>
          <input type="time" value={values.time} onChange={set('time')} />
        </Field>
        <Field label={g.location} hint={t.common.optional}>
          <input value={values.location} onChange={set('location')} />
        </Field>
      </div>
      <div className="row">
        <Field label={g.repeat}>
          <select value={values.repeat} onChange={(e) => setValues((v) => ({ ...v, repeat: e.target.value as typeof v.repeat }))}>
            {(['none', ...SeriesEvery.options] as const).map((x) => (
              <option key={x} value={x}>
                {g.repeats[x]}
              </option>
            ))}
          </select>
        </Field>
        {values.repeat !== 'none' && (
          <Field label={g.count}>
            <input inputMode="numeric" value={values.count} onChange={set('count')} />
          </Field>
        )}
      </div>
      {dates.length > 0 && <p className="muted small">{g.seriesPreview(dates.map((d) => date(d)).join(', '))}</p>}
      {(create.isError || series.isError) && <Alert>{errorMessage(create.error ?? series.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending || series.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

/** Co-owners: the assemblies they are convened to, with the agenda. */
function MemberAssemblies() {
  const { t, locale } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const g = t.governance;
  const assemblies = useQuery({ queryKey: [s.tenantId, 'governance', 'member-assemblies'], queryFn: () => api<MemberAssembly[]>(`${s.api}/governance/assemblies`) });
  return (
    <>
      <PageHeader title={g.memberTitle} intro={g.memberIntro} />
      <BoardComposition />
      {assemblies.isPending && <Loading label={t.loading} />}
      {assemblies.isError && <Alert>{errorMessage(assemblies.error, t)}</Alert>}
      {assemblies.data?.length === 0 && <Empty>{g.memberNone}</Empty>}
      {assemblies.data?.map((a) => (
        <section key={a.id} className="panel member-assembly">
          <p>
            <span className={`badge badge-meeting-${a.kind}`}>{g.kinds[a.kind]}</span> <span className={`badge badge-mstatus-${a.status}`}>{g.statuses[a.status]}</span>
          </p>
          <h2>{a.title}</h2>
          <p className="muted">
            {meetingWhen(a.scheduledAt, locale)}
            {a.location && ` · ${a.location}`}
          </p>
          <ol className="member-agenda">
            {a.items.map((i) => (
              <li key={i.position}>
                <strong>{i.title}</strong>
                {i.description && <p className="muted small">{i.description}</p>}
                {i.proposedResolution && <p className="resolution small">{i.proposedResolution}</p>}
              </li>
            ))}
          </ol>
          {a.minutesPublished && (
            <p>
              <Link to={`${s.base}/documents`}>{g.minutesInDocuments}</Link>
            </p>
          )}
        </section>
      ))}
    </>
  );
}
